import { useEffect, useState } from "react";

import { Loader2, Save } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useSaveInfraSettings } from "@/features/servers/hooks/useInfraSettings";
import {
  COMMON_UBUNTU_VERSIONS,
  DEFAULT_OS_SUPPORT_POLICY,
  OS_FAMILY_LABELS,
  OS_VERSION_PATTERN,
  type IOsSupportPolicy,
  type OsFamily,
} from "@/features/servers/schemas/servers";

interface OsSupportPolicyEditorProps {
  policy: IOsSupportPolicy;
}

/**
 * Política global: Ubuntu recomendado vs mínimo que aún recibe updates.
 */
export function OsSupportPolicyEditor({ policy }: OsSupportPolicyEditorProps) {
  const saveSettings = useSaveInfraSettings();
  const [draft, setDraft] = useState<IOsSupportPolicy>(
    policy ?? DEFAULT_OS_SUPPORT_POLICY
  );

  useEffect(() => {
    setDraft(policy ?? DEFAULT_OS_SUPPORT_POLICY);
  }, [policy]);

  const handleSave = async () => {
    if (!OS_VERSION_PATTERN.test(draft.recommended.trim())) {
      toast.error("Recomendada: formato YY.MM (ej. 26.04)");
      return;
    }
    if (!OS_VERSION_PATTERN.test(draft.minSupported.trim())) {
      toast.error("Mínima: formato YY.MM (ej. 20.04)");
      return;
    }
    try {
      await saveSettings.mutateAsync({
        osSupportPolicy: {
          family: draft.family,
          recommended: draft.recommended.trim(),
          minSupported: draft.minSupported.trim(),
        },
      });
      toast.success("Política de SO guardada");
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : "No se pudo guardar la política"
      );
    }
  };

  return (
    <Card>
      <CardHeader className="py-3">
        <CardTitle className="text-sm font-medium">
          Soporte de SO en servidores
        </CardTitle>
        <CardDescription>
          ViewMed suele usar Ubuntu Desktop (AnyDesk). Definí la versión
          recomendada y el mínimo con el que aún se aceptan updates de software.
          Hosts por debajo del mínimo aparecen como prioridad de atención.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3 pt-0">
        <div className="grid gap-3 sm:grid-cols-3">
          <div className="space-y-1.5">
            <Label className="text-xs text-muted-foreground">Familia</Label>
            <Select
              value={draft.family}
              onValueChange={(v) =>
                setDraft((prev) => ({ ...prev, family: v as OsFamily }))
              }
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {(Object.keys(OS_FAMILY_LABELS) as OsFamily[]).map((f) => (
                  <SelectItem key={f} value={f}>
                    {OS_FAMILY_LABELS[f]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs text-muted-foreground">
              Recomendada (más reciente)
            </Label>
            <Input
              list="os-recommended-versions"
              value={draft.recommended}
              onChange={(e) =>
                setDraft((prev) => ({ ...prev, recommended: e.target.value }))
              }
              placeholder="26.04"
              className="font-mono"
            />
            <datalist id="os-recommended-versions">
              {COMMON_UBUNTU_VERSIONS.map((v) => (
                <option key={v} value={v} />
              ))}
            </datalist>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs text-muted-foreground">
              Mínima soportada (updates)
            </Label>
            <Input
              list="os-min-versions"
              value={draft.minSupported}
              onChange={(e) =>
                setDraft((prev) => ({ ...prev, minSupported: e.target.value }))
              }
              placeholder="20.04"
              className="font-mono"
            />
            <datalist id="os-min-versions">
              {COMMON_UBUNTU_VERSIONS.map((v) => (
                <option key={v} value={v} />
              ))}
            </datalist>
          </div>
        </div>
        <p className="text-xs text-muted-foreground">
          Ejemplo: recomendada 26.04 · mínima 20.04 → un host en 22.04 recibe
          updates; uno en 18.04 no (hay que actualizar el SO).
        </p>
        <Button
          size="sm"
          variant="outline"
          onClick={() => void handleSave()}
          disabled={saveSettings.isPending}
        >
          {saveSettings.isPending ? (
            <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
          ) : (
            <Save className="h-4 w-4 mr-1.5" />
          )}
          Guardar política SO
        </Button>
      </CardContent>
    </Card>
  );
}
